import { getNavTabs } from "../components/NavTabs/navTabsConfig";
import { quicksightReportsNavTabs } from "../components/NavTabs/navTabsQuicksightReportsExtension";

/**
 * App tab scopes — one scope id per nav tab.
 * Stored in Okta group descriptions and checked when rendering NavTabs.
 */
export const APP_TAB_PERMISSIONS = [
    { id: "dashboard", name: "Dashboard" },
    { id: "demographics", name: "Demographics" },
    { id: "conditions", name: "Conditions" },
    { id: "fhirSearch", name: "FHIR Search" },
    { id: "views", name: "Views" },
    { id: "claimTracker", name: "Claim Tracker" },
    { id: "claimReview", name: "Claim Review" },
    { id: "clr", name: "CLR" },
    { id: "leadsSummary", name: "Leads Summary" },
    { id: "membersSummary", name: "Members Summary" },
    { id: "trafficCop", name: "Traffic Cop" },
    { id: "revenueCycle", name: "Revenue Cycle" },
    { id: "preScreening", name: "Pre-screening" },
    { id: "mortalityAlert", name: "Mortality Alert" },
    { id: "leadSourcePerformance", name: "Lead Source Performance" },
    { id: "outreachAndEnrollment", name: "Outreach and Enrollment" },
];

export const APP_TAB_PERMISSION_IDS = APP_TAB_PERMISSIONS.map((p) => p.id);

/** Safe default when Okta groups/scopes are missing or invalid. */
export const DEFAULT_SAFE_SCOPES = ["demographics"];

export function isUserAdmin(groups = []) {
    return groups.some((group) => String(group).toLowerCase() === "admin");
}

export function normalizeUserScopes(rawScopes = []) {
    const scopes = (rawScopes || [])
        .map((scope) => String(scope).trim())
        .filter(Boolean);

    if (scopes.includes("all")) {
        return ["all", ...APP_TAB_PERMISSION_IDS];
    }

    const known = scopes.filter((scope) => APP_TAB_PERMISSION_IDS.includes(scope));
    return known.length > 0 ? known : [...DEFAULT_SAFE_SCOPES];
}

export function userCanAccessTab(userScopes = [], tabPermission) {
    if (!tabPermission) return true;
    const scopes = normalizeUserScopes(userScopes);
    if (scopes.includes("all")) return true;
    return scopes.includes(tabPermission);
}

/** `_tab` query values mapped to RBAC scope ids. */
export const TAB_ID_TO_PERMISSION = {
    demographics: "demographics",
    conditions: "conditions",
    tags: "demographics",
    search: "fhirSearch",
    view: "views",
    claimTracker: "claimTracker",
    claimReview: "claimReview",
    closedLoop: "clr",
    leadsSummary: "leadsSummary",
    membersSummary: "membersSummary",
    trafficCop: "trafficCop",
    revenueCycle: "revenueCycle",
    preScreening: "preScreening",
    dashboard: "dashboard",
    lead_source_performance: "leadSourcePerformance",
    outreach_and_enrollment: "outreachAndEnrollment",
};

/** Top-level routes (and nested paths) mapped to a single RBAC scope. */
const PATH_TO_PERMISSION = {
    "/fhir-search": "fhirSearch",
    "/view": "views",
    "/claim-tracker": "claimTracker",
    "/claim-review": "claimReview",
    "/closed-loop": "clr",
    "/leads-summary": "leadsSummary",
    "/members-summary": "membersSummary",
    "/traffic-cop": "trafficCop",
    "/revenue-cycle": "revenueCycle",
    "/pre-screening": "preScreening",
    "/mortality-alert": "mortalityAlert",
    "/dashboard": "dashboard",
};

function normalizePathname(pathname = "") {
    const path = String(pathname).replace(/\/+$/, "");
    return path || "/";
}

/**
 * Resolve the RBAC scope required for a route.
 * Returns `null` when the route is not scope-gated (e.g. user-management).
 * Returns `{ anyOf: [...] }` when any one of the listed scopes is sufficient.
 */
export function getRequiredPermissionForRoute(pathname, search = "") {
    const path = normalizePathname(pathname);
    const tab = new URLSearchParams(search).get("_tab") || "";

    if (path === "/patient-list" || path.startsWith("/patient-list/")) {
        return TAB_ID_TO_PERMISSION[tab] || "demographics";
    }

    if (/^\/patient\/[^/]+/.test(path)) {
        return { anyOf: ["demographics", "conditions"] };
    }

    if (path === "/quicksight-reports" || path.startsWith("/quicksight-reports/")) {
        return TAB_ID_TO_PERMISSION[tab] || "leadSourcePerformance";
    }

    for (const [prefix, permission] of Object.entries(PATH_TO_PERMISSION)) {
        if (path === prefix || path.startsWith(`${prefix}/`)) {
            return permission;
        }
    }

    return null;
}

export function userCanAccessRoute(userScopes = [], requiredPermission) {
    if (!requiredPermission) {
        return true;
    }

    if (requiredPermission.anyOf) {
        return requiredPermission.anyOf.some((permission) =>
            userCanAccessTab(userScopes, permission)
        );
    }

    return userCanAccessTab(userScopes, requiredPermission);
}

export function getAccessibleNavTabs(userScopes = []) {
    const scopes = normalizeUserScopes(userScopes);
    return [
        ...getNavTabs({ counts: {} }),
        ...quicksightReportsNavTabs,
    ].filter((tab) => userCanAccessTab(scopes, tab.permission));
}

/** Human-readable tab label for access-denied toasts. */
export function getPermissionDisplayName(requiredPermission) {
    if (!requiredPermission) {
        return "this page";
    }

    if (requiredPermission.anyOf) {
        const names = requiredPermission.anyOf
            .map((id) => APP_TAB_PERMISSIONS.find((p) => p.id === id)?.name)
            .filter(Boolean);
        return names.length > 0 ? names.join(" / ") : "this page";
    }

    const found = APP_TAB_PERMISSIONS.find((p) => p.id === requiredPermission);
    return found?.name || requiredPermission;
}

/** First allowed tab href for post-login landing. */
export function getDefaultLandingPath(userScopes = []) {
    const tabs = getAccessibleNavTabs(userScopes);
    if (tabs.length > 0) {
        return tabs[0].href;
    }
    return "/patient-list?_tab=demographics";
}

/** Redirect target when a direct link targets a tab the user cannot access. */
export function getAccessDeniedRedirectPath(userScopes = []) {
    if (userCanAccessTab(userScopes, "demographics")) {
        return "/patient-list?_tab=demographics";
    }
    return getDefaultLandingPath(userScopes);
}
